import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, extractError } from '@/lib/api.js';
import { Dica } from '@/components/Dica.js';
import { TEMAS, forcaDoTema, nomeDaForca, type Dossie, type TemaDoDossie } from '@shared/conselheiro.js';

/**
 * O dossiê de preparação da manhã: o que a IA preparou para o conselheiro a
 * partir da pauta, do relato, do radar, das metas, dos indicadores e das
 * decisões. Só conselheiros veem.
 */

type Resposta = {
  estado: 'GERANDO' | 'PRONTO' | 'ERRO' | null;
  erro: string | null;
  gerado_em: string | null;
  pode_gerar: boolean;
  ia_disponivel: boolean;
  desatualizado: boolean;
  dossie: (Omit<Dossie, 'temas'> & { temas: (TemaDoDossie & { titulo: string })[] }) | null;
};

export const enquadramentoDoDossie = (tema: number | null) =>
  tema == null || !TEMAS[tema] ? null : `${TEMAS[tema]} · ${nomeDaForca(forcaDoTema(tema)!)}`;

function Lista({ titulo, itens }: { titulo: string; itens: string[] }) {
  if (!itens.length) return null;
  return (
    <div>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-ha-muted">{titulo}</p>
      <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-ha-text">
        {itens.map((x, i) => (
          <li key={i}>{x}</li>
        ))}
      </ul>
    </div>
  );
}

function Bloco({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="rounded-element border border-ha-border2 p-3">
      <p className="mb-2 text-sm font-semibold text-ha-text">{titulo}</p>
      <div className="flex flex-col gap-3">{children}</div>
    </div>
  );
}

export function DossieDaManha({ manhaId }: { manhaId: string }) {
  const qc = useQueryClient();
  const chave = ['conselheiro-dossie', manhaId];
  const q = useQuery({
    queryKey: chave,
    queryFn: async () => (await api.get<Resposta>(`/conselheiro/manhas/${manhaId}/dossie`)).data,
    refetchInterval: (query) => (query.state.data?.estado === 'GERANDO' ? 5000 : false),
  });
  const [erro, setErro] = useState<string | null>(null);
  const gerar = useMutation({
    mutationFn: async () => (await api.post<Resposta>(`/conselheiro/manhas/${manhaId}/dossie`)).data,
    onSuccess: (d) => {
      setErro(null);
      qc.setQueryData(chave, d);
    },
    onError: (e) => setErro(extractError(e).message),
  });

  if (q.isLoading || !q.data) return null;
  const r = q.data;
  const d = r.dossie;
  const gerando = r.estado === 'GERANDO';

  return (
    <section className="dossie rounded-card border border-ha-border2 p-4">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-ha-muted">
          Dossiê de preparação
          <Dica texto="A IA prepara, para cada tema da pauta, o enquadramento no método, hipóteses de causa e perguntas para os sócios. É só dos conselheiros: os sócios não veem." />
        </h3>
        <span className="flex flex-wrap items-center gap-3 text-xs">
          {r.gerado_em && !gerando ? (
            <span className="text-ha-muted">
              Gerado em{' '}
              {new Date(r.gerado_em).toLocaleString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'America/Sao_Paulo',
              })}
            </span>
          ) : null}
          {d && !gerando ? (
            <button
              type="button"
              className="text-accent underline print:hidden"
              // A impressão fica na trilha de acessos da empresa (etapa 15); o
              // Ctrl+P do navegador passa por fora, por isso aqui é só o botão.
              onClick={() => void api.get(`/conselheiro/manhas/${manhaId}/dossie/impressao`).finally(() => window.print())}
            >
              Imprimir
            </button>
          ) : null}
          {r.pode_gerar && !gerando ? (
            <button
              type="button"
              className="text-accent underline print:hidden"
              disabled={gerar.isPending}
              onClick={() => gerar.mutate()}
            >
              {d ? 'Gerar de novo' : 'Gerar o dossiê'}
            </button>
          ) : null}
        </span>
      </div>

      {gerando ? (
        <p className="mb-3 flex items-center gap-2 text-sm text-ha-subtle">
          <span aria-hidden className="inline-block h-2 w-2 animate-pulse rounded-full bg-primary" />
          Preparando o dossiê… leva cerca de um minuto.
        </p>
      ) : null}
      {r.estado === 'ERRO' ? <p className="mb-3 text-sm text-red-600">Não deu certo: {r.erro}</p> : null}
      {erro ? <p className="mb-3 text-sm text-red-600">{erro}</p> : null}
      {d && r.desatualizado && !gerando ? (
        <p className="mb-3 text-sm text-ha-subtle">
          A pauta mudou depois deste dossiê.{r.pode_gerar ? ' Gere de novo para incluir o que mudou.' : ''}
        </p>
      ) : null}
      {!d && !gerando && r.estado !== 'ERRO' ? (
        <p className="text-sm text-ha-subtle">
          {r.ia_disponivel
            ? 'O dossiê é preparado sozinho quando a pauta é confirmada. O Principal pode gerar antes.'
            : 'A IA não está ligada a este ambiente.'}
        </p>
      ) : null}

      {d ? (
        <div className="flex flex-col gap-4">
          <p className="whitespace-pre-wrap text-sm text-ha-text">{d.panorama}</p>
          <Lista titulo="Alertas" itens={d.alertas} />
          <Lista titulo="Onde os sócios divergem" itens={d.divergencias} />
          {d.temas.map((t, i) => (
            <Bloco key={t.tema_pauta_id} titulo={`${i + 1}. ${t.titulo}`}>
              {enquadramentoDoDossie(t.enquadramento.tema) ? (
                <p className="text-xs text-ha-muted">
                  Enquadramento: <strong className="text-ha-text">{enquadramentoDoDossie(t.enquadramento.tema)}</strong>
                  {t.enquadramento.justificativa ? ` — ${t.enquadramento.justificativa}` : ''}{' '}
                  <Link
                    to={`/direcao/biblioteca-do-metodo?tema=${t.enquadramento.tema}`}
                    className="whitespace-nowrap text-accent underline print:hidden"
                  >
                    Ver na Biblioteca do método
                  </Link>
                </p>
              ) : null}
              <Lista titulo="Hipóteses de causa" itens={t.hipoteses} />
              <Lista titulo="Perguntas para os sócios" itens={t.perguntas} />
              <Lista titulo="Pedir ou olhar antes" itens={t.dados} />
            </Bloco>
          ))}
        </div>
      ) : null}
    </section>
  );
}
